import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStats } from '../services/adminApi';

function StatCard({ title, value, subtitle, icon, color, delay }) {
  return (
    <div className={`stat-card animate-slide-up ${delay}`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
          <svg className={`w-6 h-6 text-${color}-400`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
          </svg>
        </div>
        <span className={`text-${color}-400 text-sm font-medium`}>↗</span>
      </div>
      <h3 className="text-3xl font-bold text-white mb-1">{value}</h3>
      <p className="text-navy-300 text-sm">{title}</p>
      {subtitle && <p className="text-navy-400 text-xs mt-1">{subtitle}</p>}
    </div>
  );
}

function QuickAction({ title, description, icon, to, color }) {
  return (
    <Link
      to={to}
      className="block p-4 rounded-lg border border-navy-600 hover:border-teal-500/50 hover:bg-navy-800/50 transition-all duration-200 group"
    >
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
          <svg className={`w-5 h-5 text-${color}-400`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
          </svg>
        </div>
        <div>
          <h4 className="font-medium text-white group-hover:text-teal-400 transition-colors">{title}</h4>
          <p className="text-sm text-navy-400">{description}</p>
        </div>
        <svg className="w-5 h-5 text-navy-500 ml-auto group-hover:text-teal-400 group-hover:translate-x-1 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadStats() {
      try {
        const data = await getStats();
        setStats(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 loading-shimmer rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-40 loading-shimmer rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-coral-600/20 flex items-center justify-center">
          <svg className="w-8 h-8 text-coral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Failed to load statistics</h3>
        <p className="text-navy-300 mb-4">{error}</p>
        <button onClick={() => window.location.reload()} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-navy-300 mt-1">Welcome back! Here's an overview of CrewBook.</p>
        </div>
        <button onClick={() => window.location.reload()} className="btn-secondary flex items-center gap-2 self-start">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats?.totalUsers?.toLocaleString() || 0}
          subtitle="Registered accounts"
          icon="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          color="teal"
          delay="stagger-1"
        />
        <StatCard
          title="Active Subscriptions"
          value={stats?.activeSubscriptions?.toLocaleString() || 0}
          subtitle={`${stats?.subscriptionRate || 0}% of total`}
          icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          color="teal"
          delay="stagger-2"
        />
        <StatCard
          title="Blocked Accounts"
          value={stats?.blockedAccounts?.toLocaleString() || 0}
          subtitle="Pending cleanup"
          icon="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
          color="coral"
          delay="stagger-3"
        />
        <StatCard
          title="Expired (60+ days)"
          value={stats?.expiredAccounts?.toLocaleString() || 0}
          subtitle="Ready for deletion"
          icon="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          color="coral"
          delay="stagger-4"
        />
      </div>

      {/* Additional stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Storage Usage</h3>
          <div className="flex items-end gap-4">
            <div className="text-3xl font-bold text-gradient">{stats?.storageUsedGB?.toFixed(2) || 0} GB</div>
            <div className="text-navy-400 text-sm mb-1">used</div>
          </div>
          <div className="mt-4 h-2 bg-navy-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(stats?.storagePercentage || 0, 100)}%` }}
            />
          </div>
          <p className="text-navy-400 text-xs mt-2">{stats?.totalDocuments?.toLocaleString() || 0} documents stored</p>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">ShipBook Connections</h3>
          <div className="flex items-end gap-4">
            <div className="text-3xl font-bold text-gradient">{stats?.shipbookConnections?.toLocaleString() || 0}</div>
            <div className="text-navy-400 text-sm mb-1">linked accounts</div>
          </div>
          <p className="text-navy-400 text-sm mt-4">
            {stats?.activeEmployments?.toLocaleString() || 0} active employments
          </p>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
          <div className="flex items-end gap-4">
            <div className="text-3xl font-bold text-gradient">{stats?.recentSignups || 0}</div>
            <div className="text-navy-400 text-sm mb-1">new users (7 days)</div>
          </div>
          <p className="text-navy-400 text-sm mt-4">
            {stats?.recentLogins || 0} logins in the past 24h
          </p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <QuickAction
            title="View All Users"
            description="Search and manage user accounts"
            icon="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            to="/users"
            color="teal"
          />
          <QuickAction
            title="Expired Accounts"
            description="Review and clean up expired accounts"
            icon="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            to="/expired"
            color="coral"
          />
          <QuickAction
            title="Bulk Operations"
            description="Create or delete multiple users at once"
            icon="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            to="/bulk"
            color="teal"
          />
          <QuickAction
            title="Run Cleanup"
            description="Delete accounts past the 60-day grace period"
            icon="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            to="/expired"
            color="coral"
          />
        </div>
      </div>
    </div>
  );
}
